import { useState, useEffect } from 'react';
import { OnboardingProviderService } from '../../../../../js/api/services/OnboardingProviderService';
import { Heading } from '../../../components/heading';
import { Text } from '../../../components/text';
import { type Step } from './StepNavigation';
import { LegalDocumentViewer } from './utils';

interface MarketplaceAgreementProps {
  stepInfo: Step;
  providerId: string;
  agreementDate?: string;
}

export const MarketplaceAgreement = ({
  stepInfo,
  providerId,
  agreementDate,
}: MarketplaceAgreementProps) => {
  const [agreementText, setAgreementText] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAgreement = async () => {
      if (!providerId) return;

      try {
        const response =
          await OnboardingProviderService.onboardingProviderMarketplaceAgreementRetrieve({
            id: providerId,
            xTz: Intl.DateTimeFormat().resolvedOptions().timeZone,
          });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const cleanedText = (response as any).text.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
        setAgreementText(cleanedText || '');
      } catch (error) {
        console.error('Error fetching agreement:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgreement();
  }, [providerId]);

  return (
    <div className="tw:flex tw:flex-col tw:p-4">
      <div className="tw:border-b tw:border-zinc-200 tw:mb-4">
        <Heading className="tw:dark:!text-zinc-300">{stepInfo.label}</Heading>
        <Text className="tw:pb-2">{stepInfo.description}</Text>
        {agreementDate && (
          <Text className="tw:italic tw:text-sm tw:text-zinc-500 tw:dark:!text-zinc-400 tw:pb-2">
            Agreement accepted on {new Date(agreementDate).toLocaleDateString()}
          </Text>
        )}
      </div>

      <div className="tw:prose tw:dark:!prose-invert tw:rounded-lg tw:p-4 tw:dark:bg-zinc-300 tw:dark:!text-white/90 tw:max-w-none">
        {isLoading ? (
          <Text>Loading agreement...</Text>
        ) : (
          <LegalDocumentViewer
            classNames="tw:h-[95vh] tw:sm:h-[60vh] tw:w-full tw:overflow-y-hidden"
            htmlContent={agreementText}
          />
        )}
      </div>
    </div>
  );
};
